import { CommonModule } from "@angular/common";
import { Component, OnInit, effect, inject } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { finalize } from "rxjs";
import { AuthService } from "../../services/auth.service";

@Component({
  selector: "app-auth",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: "./auth.component.html",
  styleUrl: "./auth.component.css"
})
export class AuthComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  mode: "login" | "signup" | "forgot" = "login";
  isBusy = false;
  error = "";
  message = "";

  readonly startupError = this.authService.startupError;

  readonly loginForm = this.fb.nonNullable.group({
    email: ["", [Validators.required, Validators.email]],
    password: ["", [Validators.required, Validators.minLength(6)]]
  });

  readonly signupForm = this.fb.nonNullable.group({
    name: ["", [Validators.required, Validators.minLength(2)]],
    email: ["", [Validators.required, Validators.email]],
    phone: [""],
    password: ["", [Validators.required, Validators.minLength(6)]]
  });

  readonly forgotForm = this.fb.nonNullable.group({
    email: ["", [Validators.required, Validators.email]]
  });

  constructor() {
    effect(() => {
      if (this.authService.isLoggedIn()) {
        void this.router.navigateByUrl("/dashboard");
      }
    });
  }

  ngOnInit() {
    if (this.authService.isLoggedIn()) {
      void this.router.navigateByUrl("/dashboard");
    }
  }

  setMode(mode: "login" | "signup" | "forgot") {
    this.mode = mode;
    this.error = "";
    this.message = "";
  }

  submitLogin() {
    if (this.loginForm.invalid || this.isBusy) {
      return;
    }

    this.isBusy = true;
    this.error = "";
    this.message = "";
    const value = this.loginForm.getRawValue();

    this.authService
      .login(value.email, value.password)
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: () => void this.router.navigateByUrl("/dashboard"),
        error: (error) => {
          this.error = error?.error?.message || error?.message || "Login failed";
        }
      });
  }

  submitSignup() {
    if (this.signupForm.invalid || this.isBusy) {
      return;
    }

    const value = this.signupForm.getRawValue();
    this.isBusy = true;
    this.error = "";
    this.message = "";

    this.authService
      .signup({
        name: value.name,
        email: value.email,
        phone: value.phone || null,
        password: value.password
      })
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: (result) => {
          this.message = result.message || "";
          if (result.requiresEmailVerification) {
            this.mode = "login";
            this.loginForm.patchValue({ email: value.email, password: "" });
            return;
          }
          void this.router.navigateByUrl("/dashboard");
        },
        error: (error) => {
          this.error = error?.error?.message || error?.message || "Signup failed";
        }
      });
  }

  submitForgot() {
    if (this.forgotForm.invalid || this.isBusy) {
      return;
    }

    this.isBusy = true;
    this.error = "";
    this.message = "";
    const value = this.forgotForm.getRawValue();

    this.authService
      .requestPasswordReset(value.email)
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: (result) => {
          this.message = result.message;
          this.mode = "login";
        },
        error: (error) => {
          this.error = error?.error?.message || error?.message || "Failed to send reset email";
        }
      });
  }

  // Social logins are disabled for now; email-only auth is supported.
}
